import { useState } from "react";
import { cn } from "@/lib/utils";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, Pencil, Trash2, RotateCw, Calendar, ChevronRight, ImagePlus, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/common/PageHeader";
import { SearchBar } from "@/components/common/SearchBar";
import { DataTable, Column } from "@/components/tables/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ConfirmDeleteDialog } from "@/components/common/ConfirmDeleteDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useListState } from "@/hooks/useListState";
import {
  useCreateAcademicYearMutation,
  useDeleteAcademicYearMutation,
  useGetAcademicYearsQuery,
  useUpdateAcademicYearMutation,
} from "@/redux/slices/academicYearApi";
import { useUploadImageMutation } from "@/redux/slices/uploadApi";
import { format } from "date-fns";
import type { AcademicYear } from "@/redux/slices/academicYearApi";
import { getFileUrl } from "@/lib/utils";

const schema = z.object({
  name: z.string().trim().min(2, "Name required").max(60),
  startDate: z.string().min(1, "Start date required"),
  endDate: z.string().min(1, "End date required"),
  isActive: z.boolean(),
  logo: z.string().optional().nullable(),
});
type FormValues = z.infer<typeof schema>;

export default function AcademicYearPage() {
  const list = useListState();
  const { data, isFetching, refetch } = useGetAcademicYearsQuery(list.queryParams());
  const [addYear, addM] = useCreateAcademicYearMutation();
  const [updateYear, updateM] = useUpdateAcademicYearMutation();
  const [deleteYear, deleteM] = useDeleteAcademicYearMutation();
  const [uploadImage, uploadM] = useUploadImageMutation();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<AcademicYear | null>(null);
  const [toDelete, setToDelete] = useState<AcademicYear | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", startDate: "", endDate: "", isActive: true, logo: "" },
  });

  const openCreate = () => {
    setEditing(null);
    form.reset({ name: "", startDate: "", endDate: "", isActive: true, logo: "" });
    setOpen(true);
  };
  const openEdit = (y: AcademicYear) => {
    setEditing(y);
    form.reset({ name: y.name, startDate: y.startDate || "", endDate: y.endDate || "", isActive: y.isActive, logo: y.logo || "" });
    setOpen(true);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("image", file);
    try {
      const res = await uploadImage({ formData, folder: "academic-years", subfolder: "logos" }).unwrap();
      form.setValue("logo", res.path);
      toast.success("Image uploaded successfully");
    } catch {
      toast.error("Image upload failed");
    }
  };

  const onSubmit = async (v: FormValues) => {
    try {
      if (editing) {
        await updateYear({ id: editing.id, body: v }).unwrap();
        toast.success("Academic year updated");
      } else {
        await addYear(v).unwrap();
        toast.success("Academic year added");
      }
      setOpen(false);
    } catch {
      toast.error("Something went wrong");
    }
  };

  const columns: Column<AcademicYear>[] = [
    { 
      key: "name", 
      header: "Year Name", 
      render: (r) => (
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-primary/10 text-primary border">
            {r.logo ? (
              <img src={getFileUrl(r.logo)} alt={r.name} className="h-full w-full object-cover" />
            ) : (
              <Calendar className="h-5 w-5" />
            )}
          </div>
          <span className="font-medium text-foreground">{r.name}</span>
        </div>
      ) 
    },
    { key: "startDate", header: "Start Date", render: (r) => <span className="text-muted-foreground">{r.startDate || "—"}</span> },
    { key: "endDate", header: "End Date", render: (r) => <span className="text-muted-foreground">{r.endDate || "—"}</span> },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.isActive ? "active" : "inactive"} /> },
    { key: "createdAt", header: "Created", render: (r) => format(new Date(r.createdAt), "dd MMM yyyy") },
  ];

  return (
    <div className="min-h-screen bg-muted/20">
      <PageHeader
        title="Academic Years"
        description="Manage study levels and program duration"
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching}>
              <RotateCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
            </Button>
            <Button onClick={openCreate} className="shadow-lg shadow-primary/20">
              <Plus className="mr-1.5 h-4 w-4" /> Add Year
            </Button>
          </div>
        }
      />
      <div className="space-y-6 p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col gap-4 rounded-2xl border bg-card p-4 shadow-sm lg:flex-row lg:items-center">
          <SearchBar 
            value={list.search} 
            onChange={list.setSearch} 
            placeholder="Search academic years..." 
            className="flex-1" 
          />
          <div className="flex flex-wrap items-center gap-3">
            <Select
              value={list.filters.isActive ?? "all"}
              onValueChange={(v) => list.setFilter("isActive", v)}
            >
              <SelectTrigger className="w-full sm:w-[150px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="true">Active Only</SelectItem>
                <SelectItem value="false">Inactive Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DataTable
          columns={columns}
          rows={data?.data ?? []}
          loading={isFetching}
          total={data?.total ?? 0}
          page={list.page}
          limit={list.limit}
          onPageChange={list.setPage}
          onLimitChange={list.setLimit}
          actions={(row) => (
            <div className="flex justify-end gap-1">
              <Button size="icon" variant="ghost" onClick={() => openEdit(row)}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="ghost" onClick={() => setToDelete(row)} className="text-destructive">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md shadow-2xl border-none p-0 overflow-hidden rounded-2xl flex flex-col max-h-[90vh]">
          <DialogHeader className="p-8 pb-4 bg-primary/5 text-left shrink-0">
            <DialogTitle className="text-2xl font-bold">{editing ? "Edit Academic Year" : "Create Academic Year"}</DialogTitle>
            <DialogDescription className="font-medium text-muted-foreground">Define a new study level for your educational program.</DialogDescription>
          </DialogHeader>
          <div className="px-8 py-4 flex-1 overflow-y-auto">
            <form id="year-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-semibold text-foreground">Year Name</Label>
                <Input id="name" className="h-11 shadow-sm" placeholder="e.g. First Year (BAMS)" {...form.register("name")} />
                {form.formState.errors.name && (
                  <p className="text-xs font-medium text-destructive">{form.formState.errors.name.message}</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="startDate" className="text-sm font-semibold text-foreground">Start Date</Label>
                  <Input id="startDate" type="date" className="h-11 shadow-sm" {...form.register("startDate")} />
                  {form.formState.errors.startDate && (
                    <p className="text-xs font-medium text-destructive">{form.formState.errors.startDate.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endDate" className="text-sm font-semibold text-foreground">End Date</Label>
                  <Input id="endDate" type="date" className="h-11 shadow-sm" {...form.register("endDate")} />
                  {form.formState.errors.endDate && (
                    <p className="text-xs font-medium text-destructive">{form.formState.errors.endDate.message}</p>
                  )}
                </div>
              </div>

              {/* ── Logo Upload ── */}
              <div className="space-y-3 rounded-xl border bg-muted/10 p-4 shadow-sm">
                <Label className="text-xs font-bold text-primary uppercase tracking-wider">Year Logo / Banner</Label>
                <div className="flex items-center gap-4">
                  <div className="relative group flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-primary/20 bg-background shadow-sm transition-all hover:border-primary/50">
                    {form.watch("logo") ? (
                      <img src={getFileUrl(form.watch("logo")!)} alt="logo preview" className="h-full w-full object-cover" />
                    ) : (
                      <ImagePlus className="h-6 w-6 text-muted-foreground/40" />
                    )}
                    {uploadM.isLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                        <Loader2 className="h-6 w-6 animate-spin text-white" />
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="absolute inset-0 cursor-pointer opacity-0"
                      onChange={handleImageUpload}
                      disabled={uploadM.isLoading}
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-medium text-foreground">Click the box to upload an image</p>
                    <p className="mt-1 text-[11px] text-muted-foreground leading-snug">
                      Stored in <code className="rounded bg-muted px-1 py-0.5 text-[10px]">uploads/academic-years/logos/</code>
                    </p>
                    {form.watch("logo") && (
                      <button
                        type="button"
                        onClick={() => form.setValue("logo", "")}
                        className="mt-2 flex items-center gap-1 text-[11px] text-destructive hover:underline"
                      >
                        <X className="h-3 w-3" /> Remove image
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-foreground">Accessibility Status</Label>
                <Select
                  value={form.watch("isActive") ? "true" : "false"}
                  onValueChange={(v) => form.setValue("isActive", v === "true")}
                >
                  <SelectTrigger className="h-11 shadow-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Active (Visible to Students)</SelectItem>
                    <SelectItem value="false">Inactive (Management Only)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </form>
          </div>
          <DialogFooter className="border-t p-6 bg-muted/10 shrink-0">
            <Button variant="ghost" onClick={() => setOpen(false)} className="h-11 px-6 font-bold uppercase tracking-tight text-xs">Cancel</Button>
            <Button form="year-form" type="submit" disabled={addM.isLoading || updateM.isLoading || uploadM.isLoading} className="h-11 px-8 rounded-xl font-bold uppercase tracking-wider text-xs shadow-xl shadow-primary/20 group">
              {editing ? "Update Year" : "Create Year"}
              <ChevronRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(null)}
        onConfirm={async () => {
          if (!toDelete) return;
          await deleteYear(toDelete.id).unwrap();
          toast.success("Academic year removed");
          setToDelete(null);
        }}
        loading={deleteM.isLoading}
        title="Delete academic year?"
        description={`"${toDelete?.name}" will be permanently removed. This may affect associated subjects and students.`}
      />
    </div>
  );
}
